/**
 * Enhanced AdminAddClassPage using the new component system
 * Demonstrates class creation with enhanced form validation and teacher selection
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiBookOpen, FiCalendar, FiUser } from 'react-icons/fi';

// New component system imports
import {
  FormPageLayout,
  FormField,
  FormSection,
  Input,
  Card,
} from '@grcs/components';
import {
  VStack,
  HStack,
  Text,
  Badge,
  Select,
  useToast,
  SimpleGrid,
} from '@chakra-ui/react';

// Existing hooks and types
import { ClassProfile } from '@grcs/types/DataTypes';
import { UserProfile, UserRole } from '@grcs/types/UserRole';
import {
  useSemesterOperations,
  useUserOperations,
  useClassOperations,
} from '@grcs/hook/useDatabase';
import { ALL_GRADES } from '@grcs/views/const';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface ClassFormData {
  class_name: string;
  teacher_token: string;
  grade: string;
  class: string; // Additional field for class identifier
}

interface ClassCreationStats {
  totalTeachers: number;
  unassignedTeachers: number;
  assignedTeachers: number;
  currentSemester: string;
  availableGrades: number;
}

interface EnhancedTeacherProfile extends UserProfile {
  assigned_classes: ClassProfile[];
}

export const AdminAddClassPageEnhanced: React.FC = () => {
  const semesterOps = useSemesterOperations();
  const userOps = useUserOperations();
  const classOps = useClassOperations();
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState<ClassFormData>({
    class_name: '',
    teacher_token: '',
    grade: '',
    class: '',
  });
  const [errors, setErrors] = useState<Partial<ClassFormData>>({});
  const [teachers, setTeachers] = useState<EnhancedTeacherProfile[]>([]);
  const [currentYear, setCurrentYear] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<ClassCreationStats>({
    totalTeachers: 0,
    unassignedTeachers: 0,
    assignedTeachers: 0,
    currentSemester: '',
    availableGrades: 0,
  });

  // Load current semester
  useEffect(() => {
    const loadCurrentYear = async () => {
      try {
        const { data } = await semesterOps.getCurrentSemesterYear();
        if (data && data.length > 0) {
          const year = data[0].semester_year;
          setCurrentYear(year);
          setStats((prev) => ({
            ...prev,
            currentSemester: year,
          }));
        } else {
          toast({
            title: 'No Active Semester',
            description:
              'Please set an active semester in Semester Management before creating classes.',
            status: 'warning',
            duration: 8000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error('Error loading current year:', error);
        toast({
          title: 'Error Loading Semester',
          description: 'Failed to load current semester information',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    loadCurrentYear();
  }, [semesterOps, toast]);

  // Load teachers with assignment status
  useEffect(() => {
    const loadTeachersWithAssignments = async () => {
      if (!currentYear) return; // Wait for current year to be loaded

      try {
        const { data: teacherData, error: teacherError } =
          await userOps.getUsersByRole(UserRole.TEACHER);
        if (teacherError) {
          throw teacherError;
        }

        const teacherList = teacherData || [];

        // Get all classes for current semester to check teacher assignments
        const { data: classData, error: classError } =
          await classOps.getClassesBySemester(currentYear);
        if (classError) {
          console.warn(
            'Could not load classes for teacher assignment check:',
            classError,
          );
        }

        // Assignments describe workload, not eligibility: active teachers may
        // manage any number of classes in the same semester.
        const activeTeachers: EnhancedTeacherProfile[] = teacherList
          .filter((teacher) => !teacher.deleted)
          .map((teacher) => ({
            ...teacher,
            assigned_classes: (classData || []).filter(
              (cls) => cls.teacher_token === teacher.user_token,
            ),
          }))
          .sort(
            (a, b) =>
              a.assigned_classes.length - b.assigned_classes.length ||
              (a.full_name || '').localeCompare(b.full_name || ''),
          );
        setTeachers(activeTeachers);
        const availableCount = activeTeachers.filter(
          (teacher) => teacher.assigned_classes.length === 0,
        ).length;
        const assignedCount = activeTeachers.length - availableCount;

        setStats((prev) => ({
          ...prev,
          totalTeachers: activeTeachers.length,
          unassignedTeachers: availableCount,
          assignedTeachers: assignedCount,
          availableGrades: ALL_GRADES.length,
        }));

        console.log(
          `📊 Teacher stats: ${availableCount} available, ${assignedCount} assigned, ${activeTeachers.length} total`,
        );
      } catch (error: any) {
        console.error('Error loading teachers:', error);
        toast({
          title: 'Error Loading Teachers',
          description: 'Failed to load teacher list and assignments',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadTeachersWithAssignments();
  }, [userOps, classOps, currentYear, toast]);

  const handleInputChange = (field: keyof ClassFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-generate class identifier from class name
    if (field === 'class_name') {
      const classId = value
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
      setFormData((prev) => ({
        ...prev,
        class: classId,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ClassFormData> = {};

    if (!formData.class_name.trim()) {
      newErrors.class_name = 'Class name is required';
    }

    if (!formData.teacher_token) {
      newErrors.teacher_token = 'Please select a teacher';
    } else {
      // Existing class assignments do not disqualify an active teacher.
      const selectedTeacher = teachers.find(
        (t) => t.user_token === formData.teacher_token,
      );

      if (!selectedTeacher) {
        newErrors.teacher_token = 'Selected teacher not found';
      } else if (selectedTeacher.deleted) {
        newErrors.teacher_token = 'Selected teacher account is inactive';
      }
    }

    if (!formData.grade) {
      newErrors.grade = 'Please select a grade level';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const newClass: Partial<ClassProfile> = {
        class_name: formData.class_name.trim(),
        teacher_token: formData.teacher_token,
        grade: formData.grade,
        semester_year: currentYear,
        created_at: new Date().toISOString(),
        number_of_students: 0,
      };

      const { error } = await classOps.insertData(TABLE_NAMES.CLASS, newClass);

      if (error) {
        throw error;
      }

      const selectedTeacher = teachers.find(
        (t) => t.user_token === formData.teacher_token,
      );

      toast({
        title: 'Class Created Successfully',
        description: `Class "${formData.class_name}" has been created and assigned to ${selectedTeacher?.full_name}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      navigate('/admin/class');
    } catch (error: any) {
      console.error('Error creating class:', error);
      toast({
        title: 'Error Creating Class',
        description: error.message || 'Failed to create class',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/class');
  };

  const selectedTeacher = teachers.find(
    (t) => t.user_token === formData.teacher_token,
  );

  return (
    <FormPageLayout
      title="Create New Class"
      subtitle={`Add a new class for semester ${currentYear}`}
      breadcrumb={{
        items: [
          { label: 'Admin', href: '/admin' },
          { label: 'Classes', href: '/admin/class' },
          { label: 'Create', isCurrent: true },
        ],
      }}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      submitText="Create Class"
      maxWidth="1200px"
      loading={isSubmitting}
      submitDisabled={isSubmitting || isLoading}
    >
      <VStack spacing={8} align="stretch">
        {/* Overview Stats */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Card padding={4} variant="filled">
            <VStack spacing={2}>
              <HStack>
                <FiCalendar />
                <Text fontWeight="600" fontSize="sm">
                  Current Semester
                </Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold" color="blue.600">
                {stats.currentSemester || 'Loading...'}
              </Text>
            </VStack>
          </Card>

          <Card padding={4} variant="outline">
            <VStack spacing={2}>
              <HStack>
                <FiUser />
                <Text fontWeight="600" fontSize="sm">
                  Active Teachers
                </Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold" color="green.600">
                {stats.totalTeachers}
              </Text>
              <Text fontSize="xs" color="gray.600">
                {stats.assignedTeachers} with classes,{' '}
                {stats.unassignedTeachers} unassigned
              </Text>
            </VStack>
          </Card>

          <Card padding={4} variant="subtle">
            <VStack spacing={2}>
              <HStack>
                <FiBookOpen />
                <Text fontWeight="600" fontSize="sm">
                  Grade Levels
                </Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold" color="purple.600">
                {stats.availableGrades}
              </Text>
            </VStack>
          </Card>
        </SimpleGrid>

        {/* Class Information */}
        <FormSection
          title="Class Information"
          description="Basic details about the new class"
        >
          <VStack spacing={6} align="stretch">
            <FormField
              label="Class Name"
              required
              error={errors.class_name}
              helperText="A descriptive name for this class (e.g., 'Advanced Reading 5A')"
            >
              <Input
                value={formData.class_name}
                onChange={(e) =>
                  handleInputChange('class_name', e.target.value)
                }
                placeholder="Enter class name"
                leftIcon={<FiBookOpen />}
              />
            </FormField>

            <HStack spacing={6} align="flex-start">
              <FormField
                label="Grade Level"
                required
                error={errors.grade}
                flex={1}
              >
                <Select
                  value={formData.grade}
                  onChange={(e) => handleInputChange('grade', e.target.value)}
                  placeholder="Select grade level"
                >
                  {ALL_GRADES.map((grade) => (
                    <option key={grade} value={grade}>
                      Grade {grade}
                    </option>
                  ))}
                </Select>
              </FormField>

              {formData.class && (
                <FormField
                  label="Class ID"
                  helperText="Auto-generated from class name"
                  flex={1}
                >
                  <Input
                    value={formData.class}
                    isReadOnly
                    bg="gray.50"
                    placeholder="Auto-generated"
                  />
                </FormField>
              )}
            </HStack>
          </VStack>
        </FormSection>

        {/* Teacher Assignment */}
        <FormSection
          title="Teacher Assignment"
          description="Select the teacher who will manage this class"
        >
          <FormField
            label="Assigned Teacher"
            required
            error={errors.teacher_token}
            helperText={
              stats.totalTeachers === 0
                ? 'No active teachers available for assignment'
                : 'A teacher can manage multiple classes. Current assignments are shown below.'
            }
          >
            <Select
              value={formData.teacher_token}
              onChange={(e) =>
                handleInputChange('teacher_token', e.target.value)
              }
              placeholder={
                isLoading
                  ? 'Loading teachers...'
                  : stats.totalTeachers === 0
                  ? 'No available teachers'
                  : 'Select a teacher'
              }
              disabled={isLoading || stats.totalTeachers === 0}
            >
              {teachers.map((teacher) => (
                <option key={teacher.user_token} value={teacher.user_token}>
                  {teacher.full_name} ({teacher.email})
                  {` — ${teacher.assigned_classes.length} current ${
                    teacher.assigned_classes.length === 1 ? 'class' : 'classes'
                  }`}
                </option>
              ))}
            </Select>
          </FormField>

          {selectedTeacher && (
            <Card padding={4} variant="filled">
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" fontWeight="600" color="blue.600">
                  Selected Teacher:
                </Text>
                <HStack justify="space-between">
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="600">{selectedTeacher.full_name}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {selectedTeacher.email}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Current classes:{' '}
                      {selectedTeacher.assigned_classes.length
                        ? selectedTeacher.assigned_classes
                            .map((cls) => cls.class_name)
                            .join(', ')
                        : 'None'}
                    </Text>
                  </VStack>
                  <VStack spacing={1}>
                    <Badge colorScheme="green" variant="solid">
                      Active
                    </Badge>
                    <Badge colorScheme="blue" variant="outline" size="sm">
                      Teacher
                    </Badge>
                  </VStack>
                </HStack>
              </VStack>
            </Card>
          )}
        </FormSection>

        {/* Preview */}
        {formData.class_name && formData.grade && selectedTeacher && (
          <Card padding={6} variant="outline">
            <VStack spacing={4} align="stretch">
              <HStack>
                <FiUsers />
                <Text fontSize="lg" fontWeight="600">
                  Class Preview
                </Text>
              </HStack>
              <div>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  This class will be created with the following details:
                </Text>
                <VStack spacing={2} align="start" fontSize="sm">
                  <Text>
                    <strong>Name:</strong> {formData.class_name}
                  </Text>
                  <Text>
                    <strong>Grade:</strong> {formData.grade}
                  </Text>
                  <Text>
                    <strong>Teacher:</strong> {selectedTeacher.full_name}
                  </Text>
                  <Text>
                    <strong>Semester:</strong> {currentYear}
                  </Text>
                  <Text>
                    <strong>Initial Students:</strong> 0 (can be added later)
                  </Text>
                </VStack>
              </div>
            </VStack>
          </Card>
        )}
      </VStack>
    </FormPageLayout>
  );
};
